import { useCallback, useState } from 'react';
import { useProvince } from '../../hooks/useProvince';
import { formatNumber, formatCompactNumber } from '../../utils/format';
import { COLORS } from '../../constants/theme';
import { derivePillAlert } from '../ResourceBar';
import { ResourceBarSheet } from './ResourceBarSheet';

const MIN_HEIGHT = 40;

/**
 * SF-182 Component 2: `AppShellLayout`'s mobile header pill, extracted out of `ResourceBar` so
 * the two callers can compose differently — `ResourceBar`/`Layout.tsx`'s existing usage is
 * untouched (still centered under `reserveChromeGutters`' fixed-button gutters); this component
 * is `flex: 1 1 auto; min-width: 0` inside `AppShellLayout`'s header flex row, sized around the
 * two in-flow `AccountIconButton`s instead of coexisting with fixed-position ones. Reuses
 * `ResourceBar`'s `derivePillAlert` (business logic) and `ResourceBarSheet` (the expand target)
 * verbatim — only the pill's own trigger markup is a fresh, differently-composed layout.
 *
 * Truncation ladder (Decision 2): the alert chip is `flex-shrink-0` and always renders in full;
 * the acres/NW/gold cluster is the only part allowed to truncate under width pressure.
 */
export function MobileResourceSummary() {
  const { province, error } = useProvince();
  const [isSheetOpen, setIsSheetOpen] = useState(false);
  const closeSheet = useCallback(() => setIsSheetOpen(false), []);

  if (!province) {
    return (
      <div
        className="flex items-center justify-center flex-1 min-w-0"
        style={{ minHeight: MIN_HEIGHT }}
        role="group"
        aria-label="Province summary"
      >
        {error ? (
          <span className="text-sm" role="status" style={{ color: COLORS.negative }}>Unable to load province</span>
        ) : (
          <span className="text-sm" role="status" style={{ color: COLORS.text.tertiary }}>Loading...</span>
        )}
      </div>
    );
  }

  const pillAlert = derivePillAlert(province);
  const acres = province.territory.totals.acres;
  const networth = province.metadata.networth;
  const gold = province.resources.gold;

  return (
    <div
      className="flex items-center justify-center flex-1 min-w-0"
      style={{ minHeight: MIN_HEIGHT }}
      role="group"
      aria-label="Province summary"
    >
      <button
        type="button"
        data-testid="mobile-resource-summary-pill"
        aria-haspopup="dialog"
        aria-expanded={isSheetOpen}
        aria-label={`Province summary. Land ${formatNumber(acres)} acres. Networth ${formatNumber(networth)} gc. Gold ${formatNumber(gold)} gc.${pillAlert ? ` ${pillAlert.ariaLabel}` : ''}`}
        onClick={() => setIsSheetOpen(true)}
        data-pill-alert={pillAlert ? 'true' : undefined}
        className="flex items-center min-w-0 rounded-full border"
        style={{ gap: 6, padding: pillAlert ? '6px 12px' : '8px 14px', backgroundColor: COLORS.cardAlt, borderColor: COLORS.border, maxWidth: '100%' }}
      >
        <div className="flex items-center min-w-0 truncate" style={{ gap: 6 }}>
          <span aria-hidden="true">🏰</span>
          <span className="font-mono font-bold truncate" style={{ fontSize: pillAlert ? 12 : 13, color: COLORS.text.primary }}>
            {formatNumber(acres)}<span className="text-[9px] font-semibold" style={{ color: COLORS.text.tertiary }}>ac</span>
          </span>
          <span aria-hidden="true" style={{ color: COLORS.separator }}>·</span>
          <span className="font-mono font-bold truncate" style={{ fontSize: pillAlert ? 12 : 13, color: COLORS.text.primary }}>
            <span className="text-[10px]" style={{ color: COLORS.text.tertiary }}>NW</span> {formatCompactNumber(networth)}
          </span>
          <span aria-hidden="true" style={{ color: COLORS.separator }}>·</span>
          <span className="font-mono font-bold truncate" style={{ fontSize: pillAlert ? 12 : 13, color: COLORS.gold }}>
            {formatCompactNumber(gold)}<span className="text-[9px] font-semibold" style={{ color: COLORS.text.tertiary }}>gc</span>
          </span>
        </div>
        {pillAlert && (
          <span className="flex items-center flex-shrink-0" style={{ gap: 4, color: COLORS.negative }}>
            <span
              aria-hidden="true"
              className="motion-reduce:animate-none animate-pulse"
              style={{ width: 6, height: 6, borderRadius: '50%', flexShrink: 0, backgroundColor: COLORS.negative }}
            />
            <span aria-hidden="true">{pillAlert.icon}</span>
            <span className="font-mono font-bold" style={{ fontSize: 12 }}>{pillAlert.value}</span>
          </span>
        )}
        <span aria-hidden="true" className="text-[10px] flex-shrink-0" style={{ color: COLORS.text.tertiary }}>▾</span>
      </button>

      <ResourceBarSheet
        isOpen={isSheetOpen}
        onClose={closeSheet}
        province={province}
        mobileTabBarPresent
      />
    </div>
  );
}
